import { supabase } from '@/integrations/supabase/client';
import { withServiceErrorHandling, ServiceError } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';
import type { 
  Campaign, 
  CampaignInsert, 
  CampaignUpdate, 
  CampaignTemplate,
  CampaignWithPosts 
} from '@/types/database';
import type { CampaignSequenceData, TemplateData } from '@/types/sequence-data';
import type { Json } from '@/integrations/supabase/types';

export class CampaignsService {
  private static readonly TABLE = 'campaigns';
  private static readonly logger = logger.child({ service: 'CampaignsService' });

  /**
   * Get all campaigns for an organization with pagination
   * Organization-scoped query
   */
  static async getAll(organizationId: string, options?: { limit?: number; offset?: number }) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching campaigns', { organizationId, options });

      let query = supabase
        .from('campaigns')
        .select(`
          id,
          name,
          description,
          status,
          start_date,
          end_date,
          created_at,
          campaign_posts(
            post_id,
            sequence_order,
            posts(id, content, platform, status, scheduled_for)
          )
        `, { count: 'exact' })
        .eq('organization_id', organizationId)
        .is('deleted_at', null);

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 50) - 1);
      }

      const { data, error, count } = await query.order('created_at', { ascending: false });

      if (error) throw error;

      this.logger.info('Campaigns fetched', { organizationId, count: data?.length || 0 });

      return { data: data || [], count: count || 0 };
    }, { operation: 'getAll', table: this.TABLE });
  }

  /**
   * Get a single campaign by ID with full details
   */
  static async getById(id: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching campaign', { campaignId: id });

      const { data, error } = await supabase
        .from('campaigns')
        .select(`
          *,
          campaign_posts(
            id,
            sequence_order,
            posts(*)
          )
        `)
        .eq('id', id)
        .is('deleted_at', null)
        .maybeSingle();

      if (error) throw error;

      this.logger.info('Campaign fetched', { campaignId: id, found: !!data });

      return data;
    }, { operation: 'getById', table: this.TABLE });
  }

  /**
   * Get all campaign templates (global + org's custom templates)
   * Cached for 10 minutes - templates are static data
   */
  static async getTemplates(organizationId?: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching campaign templates', { organizationId });

      let query = supabase
        .from('campaign_templates')
        .select('*')
        .eq('is_active', true);

      if (organizationId) {
        // Get global templates + org's custom templates
        query = query.or(`organization_id.is.null,organization_id.eq.${organizationId}`);
      } else {
        // Only global templates
        query = query.is('organization_id', null);
      }

      const result = await query.order('category', { ascending: true });

      if (result.error) throw result.error;

      this.logger.info('Templates fetched', { count: result.data?.length || 0 });

      return result.data || [];
    }, { operation: 'getTemplates', table: 'campaign_templates' });
  }

  /**
   * Create a custom campaign template (org-scoped)
   */
  static async createCustomTemplate(template: {
    organization_id: string;
    name: string;
    description: string;
    post_count: number;
    duration_days: number;
    category: string;
    sequence_data: CampaignSequenceData;
    template_data?: TemplateData;
  }) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Creating custom template', { 
        organizationId: template.organization_id,
        name: template.name 
      });

      const { data, error } = await supabase
        .from('campaign_templates')
        .insert({
          organization_id: template.organization_id,
          name: template.name,
          description: template.description,
          post_count: template.post_count,
          duration_days: template.duration_days,
          category: template.category,
          sequence_data: template.sequence_data as unknown as Json,
          icon: 'custom',
          tags: ['custom'],
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw ServiceError.internal('No data returned from template insert');

      this.logger.info('Custom template created', { templateId: data.id });

      return data;
    }, { operation: 'createCustomTemplate', table: 'campaign_templates' });
  }

  /**
   * Create a new campaign
   */
  static async create(campaign: CampaignInsert) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Creating campaign', { 
        organizationId: campaign.organization_id,
        name: campaign.name 
      });

      const { data, error } = await supabase
        .from('campaigns')
        .insert(campaign)
        .select('*')
        .maybeSingle();

      if (error) throw error;

      this.logger.info('Campaign created', { campaignId: data?.id });

      return data;
    }, { operation: 'create', table: this.TABLE });
  }

  /**
   * Update a campaign
   */
  static async update(id: string, updates: CampaignUpdate) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Updating campaign', { campaignId: id });

      const { data, error } = await supabase
        .from('campaigns')
        .update(updates)
        .eq('id', id)
        .select('*')
        .maybeSingle();

      if (error) throw error;

      this.logger.info('Campaign updated', { campaignId: id });

      return data;
    }, { operation: 'update', table: this.TABLE });
  }

  /**
   * Delete a campaign (soft delete)
   */
  static async delete(id: string) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Soft deleting campaign', { campaignId: id });

      const { error } = await supabase
        .from(this.TABLE)
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      this.logger.info('Campaign soft deleted successfully', { campaignId: id });
      return true;
    }, { operation: 'delete', table: this.TABLE });
  }

  /**
   * Permanently delete a campaign (admin only)
   */
  static async hardDelete(id: string) {
    return withServiceErrorHandling(async () => {
      this.logger.warn('HARD deleting campaign', { campaignId: id });

      const { error } = await supabase
        .from(this.TABLE)
        .delete()
        .eq('id', id);

      if (error) throw error;

      this.logger.warn('Campaign permanently deleted', { campaignId: id });
      return true;
    }, { operation: 'hardDelete', table: this.TABLE });
  }

  /**
   * Add a post to a campaign
   */
  static async addPost(campaignId: string, postId: string, sequenceOrder: number) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Adding post to campaign', { campaignId, postId, sequenceOrder });

      const { data, error } = await supabase
        .from('campaign_posts')
        .insert({
          campaign_id: campaignId,
          post_id: postId,
          sequence_order: sequenceOrder,
        })
        .select()
        .maybeSingle();

      if (error) throw error;

      this.logger.info('Post added to campaign', { campaignId, postId });

      return data;
    }, { operation: 'addPost', table: 'campaign_posts' });
  }

  /**
   * Remove a post from a campaign
   */
  static async removePost(campaignId: string, postId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.info('Removing post from campaign', { campaignId, postId });

      const { error } = await supabase
        .from('campaign_posts')
        .delete()
        .eq('campaign_id', campaignId)
        .eq('post_id', postId);

      if (error) throw error;

      this.logger.info('Post removed from campaign', { campaignId, postId });

      return true;
    }, { operation: 'removePost', table: 'campaign_posts' });
  }

  /**
   * Get campaign analytics
   */
  static async getAnalytics(campaignId: string) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching campaign analytics', { campaignId });

      const campaign = await this.getById(campaignId);
      
      if (!campaign) {
        throw ServiceError.notFound('Campaign', campaignId);
      }

      // Get all post IDs from the campaign
      const postIds = campaign.campaign_posts?.map(cp => cp.posts?.id).filter(Boolean) || [];

      if (postIds.length === 0) {
        return {
          totalPosts: 0,
          publishedPosts: 0,
          totalEngagement: 0,
          averageEngagement: 0,
        };
      }

      const { data: analytics, error } = await supabase
        .from('post_analytics')
        .select('*')
        .in('post_id', postIds);

      if (error) throw error;

      const totalEngagement = analytics?.reduce((sum, a) => 
        sum + (a.likes || 0) + (a.comments || 0) + (a.shares || 0), 0
      ) || 0;

      const stats = {
        totalPosts: postIds.length,
        publishedPosts: campaign.campaign_posts?.filter(cp => cp.posts?.status === 'published').length || 0,
        totalEngagement,
        averageEngagement: analytics?.length ? totalEngagement / analytics.length : 0,
      };

      this.logger.info('Campaign analytics calculated', { campaignId, ...stats });

      return stats;
    }, { operation: 'getAnalytics', table: this.TABLE });
  }

  /**
   * Reschedule all posts in a campaign based on new start date and interval
   * 
   * @scalability Uses database function `batch_reschedule_campaign_posts` for O(1)
   * query complexity. Single UPDATE with window function replaces N individual updates.
   */
  static async reschedule(
    campaignId: string, 
    newStartDate: Date, 
    intervalHours: number = 24
  ): Promise<void> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Rescheduling campaign', { campaignId, newStartDate, intervalHours });

      // Use optimized database function - single query instead of N updates
      const { data: updatedCount, error } = await supabase.rpc('batch_reschedule_campaign_posts', {
        p_campaign_id: campaignId,
        p_start_date: newStartDate.toISOString(),
        p_interval_hours: intervalHours
      });

      if (error) throw error;

      if (updatedCount === 0) {
        throw ServiceError.validation('No posts found in campaign');
      }

      // Update campaign start date
      await this.update(campaignId, { start_date: newStartDate.toISOString() });

      this.logger.info('Campaign rescheduled', { 
        campaignId, 
        postsUpdated: updatedCount 
      });
    }, { operation: 'reschedule', table: this.TABLE });
  }
}
